/**
 * EmailMigrate.tsx — customer-facing IMAP migration wizard.
 *
 * Route: /dashboard/email/migrate/:domainId
 *
 * Two halves on one page:
 *   1. Submit form: customer enters their old provider's IMAP host /
 *      username / password + the destination mailbox already living on
 *      our system, and ticks an explicit consent checkbox.
 *   2. Job list: all of the customer's submitted migrations (across
 *      domains, scoped by their auth token), with status + progress
 *      polled every 10s.
 *
 * Colour policy: customer surface → logo palette only (navy + yellow
 * + white + grays). Same rules as EmailSetup. Success uses brand
 * yellow + ✓; failure uses 🚨 + navy.
 *
 * Industry convention this mirrors:
 *   - Zoho's "Bulk Migration" wizard collects per-mailbox creds + an
 *     explicit consent line.
 *   - Hostinger / FastMail both run imapsync server-side and show
 *     progress as "N of M messages."
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import { getApiUrl } from '../config/environment';
import DashShell from '../components/dash/DashShell';

interface MigrationJob {
  id: string;
  mail_domain_id: string;
  destination_email: string;
  source_host: string;
  source_port: number;
  source_use_ssl: boolean;
  source_username: string;
  status: 'queued' | 'running' | 'succeeded' | 'failed' | 'cancelled';
  total_messages: number | null;
  transferred_messages: number | null;
  attempt_count: number;
  last_error: string | null;
  last_progress_line: string | null;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
}

interface DomainSummary { id: string; domain: string; }

const POLL_MS = 10_000;

const fmtAgo = (iso: string | null) => {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

const statusChip = (status: MigrationJob['status']) => {
  // All five states mapped into the logo palette:
  //   succeeded → brand yellow + ✓
  //   running   → navy + spinner glyph
  //   queued    → light navy tint + ·
  //   failed    → deep yellow bg, navy text, 🚨
  //   cancelled → muted gray
  switch (status) {
    case 'succeeded':
      return <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-secondary-500 text-primary-navy font-semibold">✓ Succeeded</span>;
    case 'running':
      return <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-primary-navy text-secondary-300 font-semibold">↻ Running</span>;
    case 'queued':
      return <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-secondary-100 text-primary-navy">⏳ Queued</span>;
    case 'failed':
      return <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-secondary-200 text-primary-navy font-semibold border border-primary-navy">🚨 Failed</span>;
    case 'cancelled':
      return <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">· Cancelled</span>;
  }
};

const ProgressBar: React.FC<{ transferred: number | null; total: number | null }> = ({ transferred, total }) => {
  if (!total || total < 1) return null;
  const t = transferred || 0;
  const pct = Math.min(100, Math.round((t / total) * 100));
  return (
    <div className="mt-2">
      <div className="flex justify-between text-xs text-gray-600 mb-1">
        <span>{t.toLocaleString()} of {total.toLocaleString()} messages</span>
        <span className="font-mono">{pct}%</span>
      </div>
      <div className="h-1.5 bg-gray-100 rounded overflow-hidden">
        <div className="h-full bg-secondary-500 transition-all" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

interface FormState {
  destinationEmail: string;
  destinationPassword: string;
  sourceHost: string;
  sourcePort: string;
  sourceUseSsl: boolean;
  sourceUsername: string;
  sourcePassword: string;
  consent: boolean;
}

const emptyForm: FormState = {
  destinationEmail: '',
  destinationPassword: '',
  sourceHost: '',
  sourcePort: '993',
  sourceUseSsl: true,
  sourceUsername: '',
  sourcePassword: '',
  consent: false,
};

const EmailMigrate: React.FC = () => {
  // Two route shapes:
  //   /dashboard/email/migrate/:domainId     (v1: customer_mail_domains)
  //   /dashboard/email/migrate/v2/:id        (v2: mx1_mail_domains — Business Email v2)
  const params = useParams<{ domainId?: string; id?: string }>();
  const domainId = params.domainId || params.id;
  const location = useLocation();
  const isV2 = location.pathname.includes('/migrate/v2/');
  const [domain, setDomain] = useState<DomainSummary | null>(null);
  const [domainError, setDomainError] = useState<string | null>(null);
  const [jobs, setJobs] = useState<MigrationJob[]>([]);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [listError, setListError] = useState<string | null>(null);

  const token = () => localStorage.getItem('token');

  // Load the destination domain name + the customer's jobs together.
  // v2 reads the single-domain setup payload; v1 still lists the legacy
  // customer_mail_domains and picks ours out.
  const loadAll = useCallback(async () => {
    if (!domainId) return;
    try {
      const domainUrl = isV2 ? `/email/setup/v2/${domainId}` : '/email/setup';
      const [domR, jobsR] = await Promise.all([
        fetch(getApiUrl(domainUrl), { headers: { Authorization: `Bearer ${token()}` } }),
        fetch(getApiUrl('/email/migrations'), { headers: { Authorization: `Bearer ${token()}` } }),
      ]);
      const domJ = await domR.json().catch(() => ({}));
      let resolvedDomain: DomainSummary | null = null;
      if (isV2) {
        if (domJ.success && domJ.data?.domain) {
          resolvedDomain = { id: domJ.data.domain.id, domain: domJ.data.domain.domain };
        } else {
          setDomainError(domJ.error || `Couldn't load this domain (HTTP ${domR.status})`);
        }
      } else if (domJ.success && Array.isArray(domJ.data)) {
        resolvedDomain = domJ.data.find((d: DomainSummary) => d.id === domainId) || null;
        if (!resolvedDomain) setDomainError('Domain not found');
      } else {
        setDomainError(domJ.error || `Couldn't load this domain (HTTP ${domR.status})`);
      }
      if (resolvedDomain) { setDomain(resolvedDomain); setDomainError(null); }

      const jobsJ = await jobsR.json().catch(() => ({}));
      if (jobsR.ok && jobsJ.success) {
        // Filter to jobs for THIS domain — the API returns all of the
        // customer's jobs across all their domains; UI scopes to one.
        // Match by row id, falling back to the destination address suffix
        // (v2 jobs are keyed by the mx1 row id).
        const suffix = resolvedDomain ? `@${resolvedDomain.domain.toLowerCase()}` : null;
        const filtered = (jobsJ.data as MigrationJob[]).filter(j =>
          j.mail_domain_id === domainId ||
          (!!suffix && (j.destination_email || '').toLowerCase().endsWith(suffix)));
        setJobs(filtered);
        setListError(null);
      } else {
        setListError(jobsJ.error || 'Failed to load migrations');
      }
    } catch (e: any) {
      setListError(e?.message || 'Network error');
    }
  }, [domainId, isV2]);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => {
    const t = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      loadAll();
    }, POLL_MS);
    return () => clearInterval(t);
  }, [loadAll]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.consent) { setSubmitError('Please confirm you authorize us to access this account'); return; }
    setSubmitting(true);
    setSubmitError(null);
    try {
      const r = await fetch(getApiUrl('/email/migrations'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token()}`,
        },
        body: JSON.stringify({
          mailDomainId: domainId,
          destinationEmail: form.destinationEmail.trim().toLowerCase(),
          destinationPassword: form.destinationPassword,
          sourceHost: form.sourceHost.trim().toLowerCase(),
          sourcePort: Number(form.sourcePort) || 993,
          sourceUseSsl: form.sourceUseSsl,
          sourceUsername: form.sourceUsername.trim(),
          sourcePassword: form.sourcePassword,
          customerConsent: form.consent,
        }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.success) { setSubmitError(j.error || `Submission failed (HTTP ${r.status})`); return; }
      // Clear sensitive fields; leave the host/username so the user can
      // queue a second mailbox quickly with similar creds.
      setForm({ ...emptyForm, sourceHost: form.sourceHost, sourcePort: form.sourcePort, sourceUseSsl: form.sourceUseSsl });
      await loadAll();
    } catch (e: any) {
      setSubmitError(e?.message || 'Network error');
    } finally {
      setSubmitting(false);
    }
  };

  const cancel = async (jobId: string) => {
    if (!window.confirm('Cancel this migration? Already-copied messages stay; in-flight stops.')) return;
    try {
      const r = await fetch(getApiUrl(`/email/migrations/${jobId}/cancel`), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        setListError((j as any)?.error || `Could not cancel this migration (HTTP ${r.status})`);
      }
    } catch (e: any) {
      setListError(e?.message || 'Network error while cancelling');
    }
    await loadAll();
  };

  // Delta sync: re-queue a finished job. imapsync resumes via UID matching
  // so only mail that arrived since the previous run gets transferred.
  // This is the "cutover button" for customers who switch MX a day or two
  // after the initial bulk migration completes.
  const resync = async (jobId: string) => {
    if (!window.confirm('Re-run this migration to pull in any new mail at the source? Already-copied messages are skipped automatically.')) return;
    try {
      const r = await fetch(getApiUrl(`/email/migrations/${jobId}/resync`), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        setListError((j as any)?.error || 'Could not resync this migration.');
        return;
      }
    } catch (e: any) {
      setListError(e?.message || 'Network error while resyncing');
      return;
    }
    await loadAll();
  };

  return (
    <DashShell
      title="Migrate mail from another provider"
      subtitle={
        domain
          ? `Copy mailbox contents from your old IMAP host to ${domain.domain}. One job per mailbox; runs server-side.`
          : domainError ? 'Domain not loaded' : 'Loading…'
      }
    >
      <div className="space-y-6">
        <div className="flex flex-wrap gap-4">
          <Link to="/customer/email" className="text-sm text-primary-navy hover:underline">
            ← Email dashboard
          </Link>
          <Link
            to={isV2 ? `/dashboard/email/mailboxes/v2/${domainId}` : `/dashboard/email/mailboxes/${domainId}`}
            className="text-sm text-primary-navy hover:underline"
          >
            Mailboxes
          </Link>
          <Link
            to={isV2 ? `/dashboard/email/setup/v2/${domainId}` : `/dashboard/email/setup/${domainId}`}
            className="text-sm text-primary-navy hover:underline"
          >
            Setup wizard
          </Link>
        </div>

        {domainError && (
          <div role="alert" className="bg-secondary-100 border border-secondary-500 rounded-lg p-3 text-sm text-primary-navy">
            🚨 {domainError}
          </div>
        )}

      <form onSubmit={submit} className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 space-y-4">
        <h3 className="text-lg font-semibold text-primary-navy">New migration</h3>

        {/* Two columns: destination (left) and source (right). Each side
            is grouped visually so customers don't paste source creds in
            destination fields. */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="space-y-3">
            <div className="text-xs uppercase tracking-wider text-gray-500 font-medium">Destination (on 12and22)</div>
            <div>
              <label className="block text-sm text-primary-navy mb-1">Mailbox email</label>
              <input
                required
                type="email"
                value={form.destinationEmail}
                onChange={e => setForm(s => ({ ...s, destinationEmail: e.target.value }))}
                placeholder={domain ? `you@${domain.domain}` : 'you@your-domain'}
                aria-label="Destination mailbox email"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-primary-navy focus:outline-none focus:ring-2 focus:ring-secondary-500"
              />
            </div>
            <div>
              <label className="block text-sm text-primary-navy mb-1">Mailbox password</label>
              <input
                required
                type="password"
                autoComplete="new-password"
                value={form.destinationPassword}
                onChange={e => setForm(s => ({ ...s, destinationPassword: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-primary-navy focus:outline-none focus:ring-2 focus:ring-secondary-500"
              />
              <p className="text-xs text-gray-500 mt-1">The password you set when you created this mailbox.</p>
            </div>
          </div>

          <div className="space-y-3">
            <div className="text-xs uppercase tracking-wider text-gray-500 font-medium">Source (your old provider)</div>
            <div>
              <label className="block text-sm text-primary-navy mb-1">IMAP host</label>
              <input
                required
                value={form.sourceHost}
                onChange={e => setForm(s => ({ ...s, sourceHost: e.target.value }))}
                placeholder="imap.zoho.com, imap.gmail.com, mail.example.com…"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-primary-navy focus:outline-none focus:ring-2 focus:ring-secondary-500"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-sm text-primary-navy mb-1">Port</label>
                <input
                  required
                  type="number"
                  value={form.sourcePort}
                  onChange={e => setForm(s => ({ ...s, sourcePort: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-primary-navy font-mono focus:outline-none focus:ring-2 focus:ring-secondary-500"
                />
              </div>
              <div>
                <label className="block text-sm text-primary-navy mb-1">SSL/TLS</label>
                <label className="inline-flex items-center gap-2 py-2">
                  <input
                    type="checkbox"
                    checked={form.sourceUseSsl}
                    onChange={e => setForm(s => ({ ...s, sourceUseSsl: e.target.checked }))}
                    className="accent-primary-navy"
                  />
                  <span className="text-sm text-primary-navy">Use SSL</span>
                </label>
              </div>
            </div>
            <div>
              <label className="block text-sm text-primary-navy mb-1">Username</label>
              <input
                required
                value={form.sourceUsername}
                onChange={e => setForm(s => ({ ...s, sourceUsername: e.target.value }))}
                placeholder="usually your full old email address"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-primary-navy focus:outline-none focus:ring-2 focus:ring-secondary-500"
              />
            </div>
            <div>
              <label className="block text-sm text-primary-navy mb-1">Password</label>
              <input
                required
                type="password"
                autoComplete="new-password"
                value={form.sourcePassword}
                onChange={e => setForm(s => ({ ...s, sourcePassword: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-primary-navy focus:outline-none focus:ring-2 focus:ring-secondary-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                If your provider requires an app-specific password (Gmail with 2FA, etc.), generate one and use it here.
              </p>
            </div>
          </div>
        </div>

        {/* The consent checkbox is intentionally prominent — it's the
            legal-grade authorization for us to log into a third-party
            account on the customer's behalf. */}
        <label className="flex items-start gap-3 p-3 bg-secondary-50 border border-secondary-200 rounded-lg">
          <input
            type="checkbox"
            checked={form.consent}
            onChange={e => setForm(s => ({ ...s, consent: e.target.checked }))}
            className="mt-0.5 accent-primary-navy"
          />
          <span className="text-sm text-primary-navy">
            I authorize 12and22 to connect to the source IMAP account above using these credentials
            and to copy its mailbox contents into my destination mailbox. I understand the password
            is stored encrypted and used only for this migration.
          </span>
        </label>

        {submitError && (
          <div role="alert" className="bg-secondary-100 border border-secondary-500 rounded-lg p-3 text-sm text-primary-navy">
            🚨 {submitError}
          </div>
        )}

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={submitting}
            className="bg-secondary-500 text-primary-navy font-medium px-5 py-2 rounded-lg hover:bg-secondary-600 disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            {submitting ? 'Submitting…' : 'Start migration'}
          </button>
        </div>
      </form>

      {/* Jobs list */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
        <h3 className="text-lg font-semibold text-primary-navy mb-3">Your migrations</h3>
        {listError && (
          <div role="alert" className="bg-secondary-100 border border-secondary-500 rounded-lg p-3 text-sm text-primary-navy mb-3">
            🚨 {listError}
          </div>
        )}
        {jobs.length === 0 ? (
          <div className="text-sm text-gray-500 py-6 text-center">No migrations yet — submit one above.</div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {jobs.map(j => (
              <li key={j.id} className="py-3">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="text-sm text-primary-navy break-all">
                      <span className="font-mono">{j.source_username}</span>
                      <span className="text-gray-400"> @ </span>
                      <span className="font-mono">{j.source_host}:{j.source_port}</span>
                      <span className="text-gray-400"> → </span>
                      <span className="font-mono">{j.destination_email}</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      submitted {fmtAgo(j.created_at)}
                      {j.attempt_count > 1 && <> · attempt {j.attempt_count}</>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {statusChip(j.status)}
                    {(j.status === 'queued' || j.status === 'running') && (
                      <button
                        onClick={() => cancel(j.id)}
                        className="text-xs text-primary-navy hover:underline"
                      >
                        Cancel
                      </button>
                    )}
                    {(j.status === 'succeeded' || j.status === 'failed' || j.status === 'cancelled') && (
                      <button
                        onClick={() => resync(j.id)}
                        className="text-xs text-primary-navy hover:underline"
                        title="Re-run to pull in new mail that arrived at the source since last run"
                      >
                        Run delta sync
                      </button>
                    )}
                  </div>
                </div>
                <ProgressBar transferred={j.transferred_messages} total={j.total_messages} />
                {j.last_progress_line && (
                  <div className="text-xs font-mono text-gray-600 mt-2 break-all">
                    {j.last_progress_line}
                  </div>
                )}
                {j.last_error && (
                  <div className="text-xs text-primary-navy mt-2 bg-secondary-50 border border-secondary-200 rounded p-2 break-all">
                    <strong>Error:</strong> {j.last_error}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
        </div>
      </div>
    </DashShell>
  );
};

export default EmailMigrate;
